/**
 * The hull surface recipe in TSL (asset contract §4.5, style guide §4, §6, §8): paint zones, trim bands, procedural
 * panel lines, detail normal, edge wear to primer then bare metal, cavity grime, soot fans and heat tint from the
 * ENG_ sockets, micrometeoroid pitting, sun bleaching, roughness breakup, clearcoat, runtime damage and the
 * shadow-side rim term. Everything is object space (wear sticks to the ship under the floating origin, D-011),
 * modulates a cause mask (never a uniform overlay, style guide §8.3) and fades its high frequencies between
 * 20 and 60 m (no shimmer). Tiers per preset come from materials.json `presets`.
 */
import type { Node, Texture } from 'three/webgpu'
import {
  Fn,
  If,
  abs,
  attribute,
  clamp,
  color,
  dot,
  exp2,
  float,
  floor,
  fract,
  fwidth,
  length,
  luminance,
  max,
  min,
  mix,
  modelNormalMatrix,
  mx_cell_noise_float,
  mx_fractal_noise_float,
  mx_noise_float,
  mx_worley_noise_vec2,
  normalLocal,
  normalMap,
  normalView,
  normalize,
  positionLocal,
  positionView,
  positionViewDirection,
  pow,
  saturate,
  select,
  smoothstep,
  texture,
  uv,
  vec2,
  vec3,
} from 'three/tsl'
import type { LoadedTextureSet } from '../../../assets/loader'
import { LARGE_CHIP_FREQ, type PresetTier } from '../data'
import { MAX_HULL_HITS } from '../lookState'
import { EDGE_DIST_ATTR, EDGE_FLAG_ATTR } from './edgeBake'
import type { HullContext, PaletteUniforms, TrimFamily, Variant } from './context'
import { BAND_CELLS, BAND_KIND } from './layout'
import { ENGINE_KIND, MAX_ENGINES } from './objectData'

type F = Node<'float'>
type V3 = Node<'vec3'>
/** Colour uniforms and constants are `color` nodes; the recipe works in vec3. */
export type ColorLike = Node<'vec3'> | Node<'color'>

/** A colour node as vec3 (identical at runtime; the typings keep `color` and `vec3` apart). */
export function asV3(c: ColorLike): V3 {
  return c as unknown as V3
}

/** Noise remapped to 0..1; octave 0 = the constant 0.5 (Low tier: masks only). */
export function fbm01(p: V3, octaves: number): F {
  if (octaves <= 0) return float(0.5)
  return saturate(mx_fractal_noise_float(p, octaves, 2, 0.5).mul(0.5).add(0.5))
}

/** Straw → bronze → blue → cooled grey (style guide §4 heat-tinted nozzle metal); h = 1 at the throat. */
export function heatRamp(ramp: readonly [string, string, string, string], h: F): V3 {
  let c: V3 = mix(color(ramp[3]), color(ramp[2]), smoothstep(0, 0.35, h))
  c = mix(c, color(ramp[1]), smoothstep(0.35, 0.65, h))
  return mix(c, color(ramp[0]), smoothstep(0.65, 0.95, h))
}

/** 1 near the camera, 0 beyond: the high-frequency fade of style guide §8.3 (20–60 m by default). */
export function nearFade(range: readonly [number, number]): F {
  return float(1).sub(smoothstep(range[0], range[1], positionView.length()))
}

/** Convex (x) and concave (y) edge proximity from the fallback edge field (edgeBake.ts), width in metres. */
function fallbackEdges(convexWidth: F, concaveWidth: F): Node<'vec2'> {
  const d = attribute(EDGE_DIST_ATTR, 'vec3')
  const f = attribute(EDGE_FLAG_ATTR, 'vec3')
  const near = (dist: F, w: F): F => float(1).sub(smoothstep(0, w, dist))
  const cx = max(max(f.x.max(0).mul(near(d.x, convexWidth)), f.y.max(0).mul(near(d.y, convexWidth))), f.z.max(0).mul(near(d.z, convexWidth)))
  const cv = max(max(f.x.negate().max(0).mul(near(d.x, concaveWidth)), f.y.negate().max(0).mul(near(d.y, concaveWidth))), f.z.negate().max(0).mul(near(d.z, concaveWidth)))
  return vec2(cx, cv)
}

/** Everything the paint layers need to know about the texel underneath. */
export interface BaseLayer {
  albedo: V3
  metalness: F
  roughness: F
  ao: F
  /** 1 where the texel takes paint (a paint zone on a paintable band with trim A = 1). */
  painted: F
  /** 1 on plain_paint (or untrimmed) painted panels: where procedural panel lines may go. */
  plain: F
  /** Tangent-space normal (−1..1) from trim and detail maps, or null (geometry normal). */
  tangentNormal: V3 | null
  /** Trim emissive (linear, before the band's light tint), or null. */
  emissive: V3 | null
  /** Light-strip band flag (fallback glow when the trim set is a carrier). */
  lightStrip: F
  /** Paint zone 0..3 (primary, secondary, accent, metal) from ECL_Mask.a. */
  zone: F
}

/** The weathering masks shared by the hull and decal materials. */
export interface Weathering {
  edge: F
  /** Fighter-class frequencies × this: 1.6 m / the model's primary panel pitch (style guide §6.2). */
  scale: F
  cavity: F
  near: F
  seed: F
  soot: F
  heat: F
  scorch: F
  cracks: F
}

function trimSetFor(ctx: HullContext, family: TrimFamily): LoadedTextureSet | null {
  return family === 'first_rush' ? ctx.sets.trimFirstRush : ctx.sets.trimReach
}

function tex(t: Texture | undefined, uvNode: Node<'vec2'>): Node<'vec4'> | null {
  return t ? texture(t, uvNode) : null
}

/** Paint zones and trim bands (contract §2.7, plan §2.8). */
export function baseLayer(ctx: HullContext, pal: PaletteUniforms, family: TrimFamily, tier: PresetTier, variant: Variant): BaseLayer {
  const data = ctx.data
  const mask = attribute('color', 'vec4')
  const zone = mask.a.mul(3).round()
  const metalZone = select(zone.greaterThan(2.5), float(1), float(0))
  // Oxidised alloy behaves mostly like a dielectric oxide layer (first_rush), so it is not a black mirror in space.
  const bareMetalness: F = float(1).sub(pal.uOxidation.mul(0.75))
  const zoneColor: V3 = asV3(select(zone.lessThan(0.5), pal.uPaintPrimary, select(zone.lessThan(1.5), pal.uPaintSecondary, select(zone.lessThan(2.5), pal.uPaintAccent, pal.uMetalTint))))
  const zoneRough = select(
    zone.lessThan(0.5),
    pal.uPaintRoughness.x,
    select(zone.lessThan(1.5), pal.uPaintRoughness.y, select(zone.lessThan(2.5), pal.uPaintRoughness.z, pal.uMetalRoughness)),
  )
  const uv0 = uv()
  const set = variant.trimmed ? trimSetFor(ctx, family) : null
  const tb = set ? tex(set.maps.baseColor, uv0) : null
  const torm = set ? tex(set.maps.orm, uv0) : null
  const tn = set ? tex(set.maps.normal, uv0) : null
  const te = set ? tex(set.maps.emissive, uv0) : null

  let paintMask: F = float(1)
  let plain: F = float(1)
  let lightStrip: F = float(0)
  let unpaintedAlbedo: V3 = asV3(pal.uMetalTint)
  let unpaintedMetal: F = float(1)
  let unpaintedRough: F = pal.uMetalRoughness
  if (variant.trimmed) {
    const cell = clamp(floor(uv0.y.mul(BAND_CELLS)), 0, BAND_CELLS - 1).toInt()
    const bA = ctx.bandA.element(cell)
    const bB = ctx.bandB.element(cell)
    const kind = bB.w
    plain = select(kind.lessThan(BAND_KIND.plainPaint + 0.5), float(1), float(0))
    lightStrip = bB.z
    paintMask = tb ? bA.w.mul(tb.a) : bA.w
    if (tb && torm) {
      unpaintedAlbedo = tb.rgb
      unpaintedMetal = torm.b
      unpaintedRough = torm.g
    } else {
      // Carrier trim set: style guide §4 class values per band; the nozzle band carries the heat gradient along U.
      const nozzle = select(abs(kind.sub(BAND_KIND.nozzleHeat)).lessThan(0.5), float(1), float(0))
      unpaintedAlbedo = mix(bA.xyz, heatRamp(data.globals.heatRamp, float(1).sub(saturate(fract(uv0.x).mul(5)))), nozzle)
      unpaintedMetal = bB.x
      unpaintedRough = bB.y
    }
  }
  const neutral = data.trim.neutralAlbedo
  const paintedAlbedo: V3 = tb ? zoneColor.mul(tb.rgb.div(neutral)) : zoneColor
  const paintedRough: F = torm ? zoneRough.add(torm.g.sub(data.trim.neutralRoughness)) : zoneRough
  const albedo = mix(unpaintedAlbedo, paintedAlbedo, paintMask)
  const metalness = mix(unpaintedMetal, metalZone.mul(bareMetalness), paintMask)
  const roughness = mix(unpaintedRough, paintedRough, paintMask)
  const ao = torm ? mask.r.mul(torm.r) : mask.r

  // glTF normal maps are +Y up; with glTF UVs (V down) and three's derivative tangent frame the green channel flips,
  // as GLTFLoader does for meshes without tangents (normalScale.y = -1).
  const flipG = vec3(1, -1, 1)
  let tangentNormal: V3 | null = tn ? tn.xyz.mul(2).sub(1).mul(flipG) : null
  const detail = tier.detailNormal && ctx.sets.detail?.maps.normal ? ctx.sets.detail.maps.normal : undefined
  if (detail) {
    const fade = nearFade(data.trim.detailFade).mul(data.trim.detailStrength)
    const dn = texture(detail, uv0.mul(data.trim.detailTiling)).xyz.mul(2).sub(1).mul(flipG)
    const base = tangentNormal ?? vec3(0, 0, 1)
    // UDN blend of the detail over the trim normal, faded 20–60 m (contract §4.5).
    tangentNormal = normalize(vec3(base.xy.add(dn.xy.mul(fade)), base.z))
  }
  return {
    albedo,
    metalness,
    roughness,
    ao,
    painted: paintMask.mul(float(1).sub(metalZone)),
    plain: plain.mul(paintMask).mul(float(1).sub(metalZone)),
    tangentNormal,
    emissive: te ? te.rgb : null,
    lightStrip: lightStrip.mul(te ? 0 : 1),
    zone,
  }
}

/** Cause masks: edges, cavities, soot and heat from the ENG_ sockets, scorch and cracks from damage. */
export function weathering(ctx: HullContext, pal: PaletteUniforms, tier: PresetTier, variant: Variant, noEdges = false): Weathering {
  const data = ctx.data
  const o = ctx.obj
  const p = positionLocal
  const seed = o.params.x
  const scale = float(1.6).div(o.params.y.max(0.1))
  const oct = tier.noiseOctaves
  const bounded = data.shaderPerf.boundSlotLoops
  // High-frequency wear fades by distance (style guide §8.3) and by pixel footprint, so a small or low-resolution
  // view averages the chips instead of sparkling.
  const footprint = length(fwidth(p)).mul(0.5).mul(scale)
  const near = min(nearFade(data.wear.fade), float(1).sub(smoothstep(data.wear.footprintFade[0], data.wear.footprintFade[1], footprint)))

  let edge: F
  let cavity: F
  if (noEdges) {
    // Decal quads: their own quad borders are not hull edges.
    edge = float(0)
    cavity = variant.baked ? attribute('color', 'vec4').b : float(0)
  } else if (variant.baked) {
    const mask = attribute('color', 'vec4')
    edge = mask.g
    cavity = mask.b
  } else {
    // Wider, noise-broken bands on the fallback edge field (the greyboxes have no bake).
    const jitter = oct > 0 ? mx_noise_float(p.mul(3.1).add(seed.mul(41))).mul(0.35).add(1) : float(1)
    // Wear bands widen with the class, but slower than the panel pitch (a station edge is not 5× as chipped).
    const widen = scale.sqrt()
    const e = fallbackEdges(float(data.wear.edgeWidth).div(widen).mul(jitter), float(data.wear.cavityWidth).div(widen))
    edge = e.x
    cavity = e.y
  }

  // Soot fans and heat tint (style guide §8.1): computed from the real socket positions every frame.
  let soot: F = float(0)
  let heat: F = float(0)
  if (tier.soot || tier.heat) {
    const streak = oct > 0 ? fbm01(vec3(p.x.mul(data.soot.streakFrequency), p.y.mul(data.soot.streakFrequency), p.z.mul(0.35)).add(seed.mul(13)), 1) : float(0.5)
    // G5 (PERF): with `boundSlotLoops` each slot runs only below the object's engine count (a uniform branch per
    // draw); slots past it are parked (kind none) and contribute exactly 0 to the max, so the result is identical.
    const engineSlot = (i: number, streakV: F): { soot: F; heat: F } => {
      const pos = o.engPos[i]
      const dir = o.engDir[i]
      const rel = p.sub(pos.xyz)
      const d = length(rel)
      const r = pos.w.max(0.05)
      const kind = dir.w
      let sootT: F = float(0)
      let heatT: F = float(0)
      const nozzle = select(kind.greaterThan(ENGINE_KIND.none + 0.5).and(kind.lessThan(ENGINE_KIND.retro + 0.5)), float(1), float(0))
      const rcs = select(kind.greaterThan(ENGINE_KIND.retro + 0.5), float(1), float(0))
      if (tier.soot) {
        // Nozzles: a soot ring around the lip plus a fan that widens downstream of the exit and streaks along the
        // flow (reference: work/codex-a5/soot_ref_nozzle.png, see docs/art/reviews/materials.md).
        const along = dot(rel, dir.xyz)
        const radialV = rel.sub(dir.xyz.mul(along))
        const radial = length(radialV)
        const lip = pow(saturate(float(1).sub(d.div(r.mul(data.soot.lipReach)))), 1.6)
        const width = r.mul(float(1.2).add(along.max(0).mul(data.soot.fanSpread)))
        const downstream = smoothstep(r.mul(-0.4), r.mul(0.2), along).mul(float(1).sub(smoothstep(0, r.mul(data.soot.nozzleReach), along)))
        const fanN = saturate(float(1).sub(radial.div(width))).mul(downstream)
        const flow = oct >= 2 ? fbm01(normalize(radialV).mul(3.2).add(dir.xyz.mul(along.mul(0.35))).add(float(i).mul(5.1)), 1) : streakV
        const nozzleSoot = max(lip.mul(0.75), fanN).mul(flow.mul(0.9).add(0.45))
        // RCS: a soft soot fan around the port, broken up by the shared streak noise (no graphic starburst).
        const fan = pow(saturate(float(1).sub(d.div(data.soot.rcsReach))), 2).mul(streakV.mul(0.8).add(0.3)).mul(0.7)
        sootT = max(nozzleSoot.mul(nozzle), fan.mul(rcs))
      }
      if (tier.heat) heatT = saturate(float(1).sub(d.div(r.mul(data.soot.heatReach)))).mul(nozzle)
      return { soot: sootT, heat: heatT }
    }
    if (bounded) {
      const sh = Fn(() => {
        const streakV = streak.toVar()
        const sootV = float(0).toVar()
        const heatV = float(0).toVar()
        for (let i = 0; i < MAX_ENGINES; i++) {
          If(o.counts.x.greaterThan(i), () => {
            const t = engineSlot(i, streakV)
            if (tier.soot) sootV.assign(max(sootV, t.soot))
            if (tier.heat) heatV.assign(max(heatV, t.heat))
          })
        }
        return vec2(sootV, heatV)
      })() as unknown as Node<'vec2'>
      soot = sh.x
      heat = sh.y
    } else {
      for (let i = 0; i < MAX_ENGINES; i++) {
        const t = engineSlot(i, streak)
        if (tier.soot) soot = max(soot, t.soot)
        if (tier.heat) heat = max(heat, t.heat)
      }
    }
    soot = saturate(soot.mul(pal.uSootAmount).mul(ctx.tune.soot).mul(1.8))
  }

  // Runtime damage (contract §4.5): scorch around the last 8 hits, glowing cracks that cool over seconds.
  const ship = o.ship
  const damage = ship.z
  const jag = oct > 0 ? fbm01(p.mul(2.3).add(seed.mul(5)), 1) : float(0.5)
  let scorch: F = float(0)
  let glow: F = float(0)
  const hitSlot = (i: number, jagV: F): { scorch: F; glow: F | null } => {
    const h = o.hits[i]
    const d = length(p.sub(h.xyz))
    const age = max(ctx.time.sub(h.w), 0)
    const r = float(data.damage.scorchRadius).mul(jagV.mul(0.7).add(0.65)).mul(damage.mul(0.5).add(0.75))
    const sc = float(1).sub(smoothstep(r.mul(0.35), r, d))
    if (!tier.cracks) return { scorch: sc, glow: null }
    const nearHit = float(1).sub(smoothstep(0, data.damage.crackRadius, d))
    return { scorch: sc, glow: nearHit.mul(exp2(age.div(data.damage.crackHalfLife).negate()).add(damage.mul(0.35))) }
  }
  if (bounded) {
    // G5: hit slots past the object's recorded hits hold the empty sentinel and contribute exactly 0.
    const sg = Fn(() => {
      const jagV = jag.toVar()
      const scorchV = float(0).toVar()
      const glowV = float(0).toVar()
      for (let i = 0; i < MAX_HULL_HITS; i++) {
        If(o.counts.y.greaterThan(i), () => {
          const t = hitSlot(i, jagV)
          scorchV.assign(max(scorchV, t.scorch))
          if (t.glow) glowV.assign(max(glowV, t.glow))
        })
      }
      return vec2(scorchV, glowV)
    })() as unknown as Node<'vec2'>
    scorch = sg.x
    glow = sg.y
  } else {
    for (let i = 0; i < MAX_HULL_HITS; i++) {
      const t = hitSlot(i, jag)
      scorch = max(scorch, t.scorch)
      if (t.glow) glow = max(glow, t.glow)
    }
  }
  // Heavy damage without recorded hits still reads: big procedural scorch patches (the palette stays identifiable).
  const worn = smoothstep(data.damage.wornThreshold, 1, damage)
  if (oct > 0) {
    const pn = fbm01(p.mul(0.45).add(seed.mul(9).add(11)), Math.min(oct, 2))
    scorch = max(scorch, smoothstep(0.6, 0.7, pn).mul(worn))
    // Only the hottest cores of old burns still glow, faintly.
    glow = max(glow, smoothstep(0.74, 0.82, pn).mul(worn).mul(0.3))
  }
  let cracks: F = float(0)
  if (tier.cracks) {
    const w = mx_worley_noise_vec2(p.mul(4.5).add(seed.mul(3)))
    const line = float(1).sub(smoothstep(0, 0.035, w.y.sub(w.x)))
    cracks = line.mul(glow).mul(near.mul(0.6).add(0.4))
  }
  return { edge, scale, cavity, near, seed, soot, heat, scorch: saturate(scorch), cracks }
}

/** Material node set a hull material receives. */
export interface SurfaceNodes {
  color: V3
  roughness: F
  metalness: F
  ao: F
  normal: V3 | null
  emissive: V3
  clearcoat: F | null
  clearcoatRoughness: F | null
}

/** Style guide §3.4: camera Fresnel rim masked to the side the star does not light. */
export function rimTerm(ctx: HullContext, pal: PaletteUniforms, ao: F): V3 {
  const ndv = saturate(dot(normalView, positionViewDirection))
  const fres = pow(float(1).sub(ndv), pal.uRimPower)
  const ndl = saturate(dot(normalView, ctx.key.xyz)).mul(ctx.key.w)
  return pal.uRimColor.mul(pal.uRimStrength.mul(ctx.tune.rim)).mul(fres.mul(float(1).sub(ndl)).mul(ao.mul(0.6).add(0.4)))
}

export function unitColorY(colorNode: ColorLike, y: F | number): V3 {
  const c: V3 = asV3(colorNode)
  const yy: F = typeof y === 'number' ? float(y) : y
  return c.mul(yy.div(max(luminance(c), 1e-3)))
}

/** The hull plating layout in object space: staggered plates at the class pitch on the dominant projection plane. */
interface Plates {
  axA: V3
  axB: V3
  ha: F
  wb: F
  row: F
  col: F
  la: F
  lb: F
  fa: F
}

function plates(ctx: HullContext): Plates {
  const o = ctx.obj
  const p = positionLocal
  const an = abs(normalLocal)
  const xDom = an.x.greaterThan(an.y).and(an.x.greaterThan(an.z))
  const yDom = an.y.greaterThan(an.z)
  // Two in-plane coordinates of the dominant projection plane and their object-space axes.
  const a = select(xDom, p.y, select(yDom, p.z, p.y))
  const b = select(xDom, p.z, select(yDom, p.x, p.x))
  const axA = select(xDom, vec3(0, 1, 0), select(yDom, vec3(0, 0, 1), vec3(0, 1, 0)))
  const axB = select(xDom, vec3(0, 0, 1), select(yDom, vec3(1, 0, 0), vec3(1, 0, 0)))
  const pitch = o.params.y
  const ha = pitch.mul(0.8)
  const wb = pitch.mul(1.25)
  const row = floor(a.div(ha))
  const la = fract(a.div(ha)).mul(ha)
  const fa = fract(a.div(ha)).sub(0.5)
  // Staggered rows; some butt seams are dropped (long plates) and some plates are split once (kd layout), so the
  // lattice reads as plating, not brickwork.
  const b2 = b.add(row.mul(wb).mul(0.5))
  const col = floor(b2.div(wb))
  const lb = fract(b2.div(wb)).mul(wb)
  return { axA, axB, ha, wb, row, col, la, lb, fa }
}

/** Seam lattice (style guide §6.2 class pitch) with an analytic groove normal. Returns (line, tilt dir). */
function panelLattice(ctx: HullContext, tier: PresetTier, pl: Plates): { line: F; delta: V3 } {
  const o = ctx.obj
  const seed = o.params.x
  const { axA, axB, ha, wb, row, col, la, lb, fa } = pl
  const dA = float(0.5).sub(abs(fa)).mul(ha)
  const keepL = mx_cell_noise_float(vec3(row, col, seed.mul(97))).greaterThan(0.3)
  const keepR = mx_cell_noise_float(vec3(row, col.add(1), seed.mul(97))).greaterThan(0.3)
  const dL = select(keepL, lb, float(1e3))
  const dR = select(keepR, wb.sub(lb), float(1e3))
  const flip = (c: Node<'bool'>): F => select(c, float(-1), float(1))
  let d: F = dA
  // Gradient of the distance field (points away from the nearest seam).
  let grad: V3 = axA.mul(flip(fa.greaterThanEqual(0)))
  grad = select(dL.lessThan(d), axB, grad)
  d = min(d, dL)
  grad = select(dR.lessThan(d), axB.negate(), grad)
  d = min(d, dR)
  const h = mx_cell_noise_float(vec3(col, row.add(17.3), seed.mul(53)))
  const h2 = mx_cell_noise_float(vec3(row.add(5.1), col, seed.mul(29)))
  const at = float(0.3).add(h2.mul(0.4))
  const dSa = select(h.greaterThan(0.4).and(h.lessThan(0.7)), abs(la.sub(ha.mul(at))), float(1e3))
  const dSb = select(h.greaterThanEqual(0.7), abs(lb.sub(wb.mul(at))), float(1e3))
  grad = select(dSa.lessThan(d), axA.mul(flip(la.lessThan(ha.mul(at)))), grad)
  d = min(d, dSa)
  grad = select(dSb.lessThan(d), axB.mul(flip(lb.lessThan(wb.mul(at)))), grad)
  d = min(d, dSb)
  if (tier.secondaryPanels) {
    // Access panels: a small inset rectangle in some plates (style guide §6.2 secondary pitch).
    const pick = mx_cell_noise_float(vec3(row.add(31.7), col, seed.mul(71)))
    const ca = la.sub(ha.mul(0.5))
    const cb = lb.sub(wb.mul(0.5))
    const half = vec2(o.params.z.mul(0.42), o.params.z.mul(0.6))
    const q = vec2(abs(ca), abs(cb)).sub(half)
    const box = length(max(q, vec2(0, 0))).add(min(max(q.x, q.y), 0))
    const dS = select(pick.lessThan(ctx.data.panelLines.secondaryShare), abs(box), float(1e3))
    const gS = select(q.x.greaterThan(q.y), axA.mul(flip(ca.lessThan(0))), axB.mul(flip(cb.lessThan(0)))).mul(flip(box.lessThan(0)))
    grad = select(dS.lessThan(d), gS, grad)
    d = min(d, dS)
  }
  const w = o.params.w.mul(0.5)
  const fw = fwidth(d).max(1e-5)
  const core = float(1).sub(smoothstep(w.sub(fw), w.add(fw), d))
  // Sub-pixel seams converge to their average coverage instead of aliasing.
  // Seams narrower than about a pixel fade out entirely (their average darkening is ~1%), so they never crawl.
  const footprint = length(fwidth(positionLocal)).mul(0.5)
  const fpFade = float(1).sub(smoothstep(w.mul(2), w.mul(6), footprint))
  const line = core.mul(saturate(w.div(fw.mul(1.5)))).mul(fpFade)
  const lip = smoothstep(w.mul(0.6), w.mul(1.1), d).mul(float(1).sub(smoothstep(w.mul(1.1), w.mul(2.4), d)))
  return { line, delta: grad.mul(lip.mul(fpFade).mul(-0.55)) }
}

/**
 * Whether a hull variant gets the procedural plate layer (plate tone, mottle, seam lattice). The hero variant (baked
 * ECL_Mask + trim-sheet UVs: the packed ships, the Hub, the hardpoints) follows `trim.proceduralPlates` (D-939: its
 * baked trim panels already carry plates and panel lines); every other variant keeps plates.
 */
export function hasProceduralPlates(data: HullContext['data'], variant: Variant): boolean {
  return !(variant.baked && variant.trimmed) || data.trim.proceduralPlates
}

/** The full hull material node set (MAT_hull). */
export function hullSurface(ctx: HullContext, pal: PaletteUniforms, family: TrimFamily, tier: PresetTier, variant: Variant): SurfaceNodes {
  const data = ctx.data
  const g = data.globals
  const base = baseLayer(ctx, pal, family, tier, variant)
  const w = weathering(ctx, pal, tier, variant)
  const p = positionLocal
  const oct = tier.noiseOctaves
  let albedo: V3 = base.albedo
  let rough: F = base.roughness
  let metal: F = base.metalness
  let ao: F = base.ao

  // Sun bleaching of up-facing paint (style guide §8.1; the Kestrel's dorsal yellow is faded). Livery bleach 0 = none.
  {
    const b = saturate(normalLocal.y).mul(pal.uBleach).mul(base.painted)
    albedo = mix(albedo, vec3(luminance(albedo)).mul(1.08), b)
  }

  // Plate-to-plate variation and large-scale age mottling of the paint: flat panels read as assembled, weathered
  // plates instead of one plastic sheet (D-047 lighting overhaul). Hero (baked + trimmed) hulls skip it with the seam
  // lattice below unless `trim.proceduralPlates` (D-939: their baked trim panels already carry plates and panel lines).
  const pl = plates(ctx)
  const plateLayer = hasProceduralPlates(data, variant)
  if (plateLayer) {
    const plateV = mx_cell_noise_float(vec3(pl.row.add(3.1), pl.col.add(7.7), w.seed.mul(43)))
    const plateN2 = mx_cell_noise_float(vec3(pl.col.add(1.3), pl.row.add(5.9), w.seed.mul(17)))
    const mottleN = oct > 0 ? fbm01(p.mul(w.scale.mul(data.wear.mottleFrequency)).add(w.seed.mul(29)), Math.min(oct, 2)) : float(0.5)
    const paintVar = float(1).add(plateV.sub(0.5).mul(2 * data.wear.plateValue)).add(mottleN.sub(0.5).mul(2 * data.wear.mottle))
    albedo = mix(albedo, albedo.mul(paintVar), base.painted)
    rough = rough.add(plateN2.sub(0.5).mul(2 * data.wear.plateRoughness).mul(base.painted))
  }

  // Tonal break-up (per livery, off by default): broad faded or resprayed patches, so a large paint field is not
  // one flat value. Low frequency and contrast-limited, so it reads as age, not noise; not on Low (no noise).
  // Livery uniforms (D-341 on the shared slot materials): amount 0 and desaturate 0 leave the albedo unchanged.
  if (oct > 0) {
    const tn = smoothstep(0.3, 0.7, fbm01(p.mul(w.scale.mul(pal.uTonalFrequency)).add(w.seed.mul(47)), Math.min(oct, 2)))
    const faded = mix(albedo, vec3(luminance(albedo)), pal.uTonalDesaturate).mul(float(1).sub(pal.uTonalAmount))
    albedo = mix(albedo, faded, tn.mul(base.painted))
  }

  // Procedural panel lines: the tertiary layer on large bare panels, masked to plain paint, faded with distance.
  let normalDelta: V3 | null = null
  if (tier.panelLines && plateLayer) {
    const lat = panelLattice(ctx, tier, pl)
    // Seams stop short of convex edges (the bevel highlight is the edge's own detail).
    const clear = float(1).sub(smoothstep(data.panelLines.edgeClear[0], data.panelLines.edgeClear[1], w.edge))
    const fade = nearFade(data.panelLines.fade)
    const amount = base.plain.mul(clear).mul(ctx.tune.panelLines).mul(fade)
    albedo = albedo.mul(float(1).sub(lat.line.mul(amount).mul(data.panelLines.darken)))
    rough = rough.add(lat.line.mul(amount).mul(0.12))
    ao = ao.mul(float(1).sub(lat.line.mul(amount).mul(0.4)))
    normalDelta = lat.delta.mul(amount)
  }

  // Edge wear (contract §4.5): convexity × object-space fractal noise, paint → primer → bare metal. Noise only
  // breaks up a cause mask (style guide §8.3): convex edges, and on very old hulls whole failed paint areas.
  // Frequencies scale with the class panel pitch, so chips are 5–40 mm on fighters and 50–150 mm on stations.
  // `chips.size` scales the class-scaled chip noise; `chips.large` adds a low-frequency octave for the few big
  // 5–15 cm chips (fighter class) that bite past the edge band.
  // A livery without a `chips` block packs the legacy values (size 1, large 0, raw G, gain 1, primer under-layer,
  // `wear.metalDepth`), so its response is unchanged.
  const chipFreq = w.scale.mul(data.wear.chipFrequency).mul(pal.uChipFreqMul)
  let chipN: F = fbm01(p.mul(chipFreq).add(w.seed.mul(71)), oct)
  if (oct > 0) {
    const big = fbm01(p.mul(chipFreq.mul(LARGE_CHIP_FREQ)).add(w.seed.mul(83)), Math.min(oct, 2))
    chipN = saturate(chipN.add(big.sub(0.5).mul(pal.uChipLarge.mul(1.5))))
  }
  const nn = mix(float(0.5), chipN, w.near)
  // Contrast threshold on the baked mask: a soft G band chips in islands instead of washing the whole rim.
  const edgeMask: F = mix(w.edge, smoothstep(pal.uChipContrastLo, pal.uChipContrastHi, w.edge), pal.uChipContrastOn).mul(pal.uChipEdge)
  const wearAmt = saturate(pal.uWear.mul(ctx.tune.wear))
  // Low-frequency variation: some edge runs stay clean, others are chewed up (handling, impacts).
  const runs = oct > 0 ? fbm01(p.mul(w.scale.mul(0.9)).add(w.seed.mul(37)), Math.min(oct, 2)) : float(0.5)
  // Failed paint areas only on very worn hulls (first_rush: 30–60% of the paint gone), favouring sun-facing sides.
  const patchN = oct > 0 ? fbm01(p.mul(w.scale.mul(0.9)).add(w.seed.mul(23)), Math.min(oct, 2)) : float(0.5)
  const oldHull = smoothstep(0.55, 0.8, wearAmt)
  // Ragged, flaky borders: the chip noise pushes the failure front in and out.
  const blobs = smoothstep(0.67, 0.71, patchN.add(chipN.sub(0.5).mul(0.16)).add(saturate(normalLocal.y).mul(0.08)).add(wearAmt.mul(0.5)).sub(0.4))
  // Paint fails plate by plate as well (derelict sheet: "paint gone from edges and whole panels").
  const plateN = mx_cell_noise_float(vec3(pl.row, pl.col, w.seed.mul(61).add(3)))
  const plateGone = select(plateN.lessThan(wearAmt.mul(0.12)), float(1), float(0))
  const islands = oct > 0 ? smoothstep(0.4, 0.52, fbm01(p.mul(w.scale.mul(2.2)).add(w.seed.mul(19)), Math.min(oct, 2)).add(chipN.sub(0.5).mul(0.2))) : float(1)
  const failed = max(blobs, plateGone.mul(islands)).mul(oldHull)
  let cause: F = max(edgeMask.mul(runs.mul(1.1).add(0.35)), failed.mul(1.1))
  if (tier.pitting) {
    // Debris strikes: sparse isolated chips on forward-facing panels (a cause, not a uniform overlay).
    const forward = smoothstep(0.1, 0.7, normalLocal.z.negate())
    const strike = smoothstep(0.8, 0.83, fbm01(p.mul(w.scale.mul(9)).add(w.seed.mul(91)), 1))
    cause = max(cause, strike.mul(forward).mul(saturate(wearAmt.mul(2.5))).mul(w.near))
  }
  const score = cause.add(nn.sub(0.5).mul(0.9).mul(saturate(cause.mul(3))))
  const threshold = float(1.05).sub(wearAmt.mul(0.95))
  const aa = fwidth(score).max(0.015).add(float(1).sub(w.near).mul(0.25))
  const primer = smoothstep(threshold.sub(aa), threshold.add(aa), score).mul(base.painted)
  const bareAt = threshold.add(pal.uChipBareDepth)
  const bare = smoothstep(bareAt.sub(aa), bareAt.add(aa), score).mul(base.painted)
  // The under-layer: the livery's per-zone colour (primer or the factory gunmetal under an overpaint), else primer.
  const under: V3 = select(base.zone.lessThan(0.5), pal.uChipUnderPrimary, select(base.zone.lessThan(1.5), pal.uChipUnderSecondary, pal.uChipUnderAccent))
  albedo = mix(albedo, under, primer)
  albedo = mix(albedo, pal.uMetalTint, bare)
  rough = mix(rough, float(g.primerRoughness), primer)
  rough = mix(rough, float(g.bareMetalRoughness), bare)
  metal = mix(metal, float(1).sub(pal.uOxidation.mul(0.75)), bare)
  // Worn edges of bare-metal zones get polished rather than chipped.
  const unpaintedEdge = w.edge.mul(float(1).sub(base.painted)).mul(metal)
  rough = rough.sub(unpaintedEdge.mul(0.12))

  // Micrometeoroid pitting on forward faces (style guide §8.1): fine pits plus a sand-blasted matte band.
  if (tier.pitting) {
    const facing = smoothstep(0.15, 0.85, normalLocal.z.negate()).mul(pal.uPitting).mul(ctx.tune.wear.min(1.5))
    const pits = mx_worley_noise_vec2(p.mul(38).add(w.seed.mul(17)))
    // Pits are millimetres wide: show them only while a pixel is smaller than a pit (no sparkle at range).
    const footprint = length(fwidth(p)).mul(0.5)
    const pit = float(1).sub(smoothstep(0.08, 0.22, pits.x)).mul(float(1).sub(smoothstep(0.002, 0.006, footprint)))
    rough = rough.add(facing.mul(0.1))
    albedo = mix(albedo, vec3(luminance(albedo)), facing.mul(0.12))
    albedo = albedo.mul(float(1).sub(pit.mul(facing).mul(0.35)))
  }

  // Cavity grime: darker, rougher, slightly desaturated.
  const grimeN = fbm01(p.mul(w.scale.mul(data.wear.grimeFrequency)).add(w.seed.mul(31)), Math.min(oct, 2))
  const grime = saturate(w.cavity.mul(grimeN.add(0.5)).mul(pal.uGrimeAmount).mul(ctx.tune.grime).mul(2.2))
  albedo = mix(albedo, pal.uGrime.mul(luminance(albedo).mul(0.5).add(0.3)), grime.mul(0.85))
  rough = rough.add(grime.mul(0.18))
  if (!variant.baked) ao = ao.mul(float(1).sub(w.cavity.mul(0.35)))

  // Thermal: heat-tinted metal and paint baked matte brown near the bells.
  if (tier.heat) {
    const onMetal = saturate(metal)
    albedo = mix(albedo, heatRamp(g.heatRamp, w.heat), w.heat.mul(onMetal).mul(0.9))
    albedo = mix(albedo, color(g.bakedPaint), smoothstep(0.25, 0.9, w.heat).mul(float(1).sub(onMetal)).mul(0.7))
    rough = rough.add(w.heat.mul(float(1).sub(onMetal)).mul(0.2))
  }

  // Soot (layer order, style guide §7.1: over paint, wear and grime).
  albedo = mix(albedo, pal.uSoot, w.soot.mul(0.88))
  rough = mix(rough, float(0.82), w.soot)
  metal = metal.mul(float(1).sub(w.soot.mul(0.8)))

  // Damage scorch.
  albedo = mix(albedo, color(g.scorch), w.scorch.mul(0.92))
  rough = mix(rough, float(0.9), w.scorch)
  metal = metal.mul(float(1).sub(w.scorch.mul(0.7)))

  // Roughness breakup ±0.08 (two octaves on High, one on Medium).
  if (oct > 0) {
    const rb = fbm01(p.mul(data.wear.breakupFrequency).add(w.seed.mul(53)), Math.min(oct, 2)).sub(0.5).mul(2 * data.wear.breakup * 1.6)
    rough = rough.add(rb)
  }
  rough = clamp(rough, 0.18, 1)

  // Normal: trim + detail maps (tangent space), plus the analytic seam grooves (object space → view).
  let normal: V3 | null = null
  if (base.tangentNormal || normalDelta) {
    const mapped: V3 = base.tangentNormal ? (normalMap(base.tangentNormal.mul(0.5).add(0.5)) as unknown as V3) : normalView
    normal = normalDelta ? normalize(mapped.add(modelNormalMatrix.mul(normalDelta))) : mapped
  }

  // Emissive: trim light strips, damage cracks and the rim term.
  let emissive: V3 = rimTerm(ctx, pal, ao)
  if (base.emissive) emissive = emissive.add(unitColorY(pal.uSensor, data.emissive.signalY * 0.5).mul(base.emissive).mul(float(1).sub(w.soot)))
  else emissive = emissive.add(unitColorY(pal.uSensor, data.emissive.signalY * 0.35).mul(base.lightStrip))
  if (tier.cracks) emissive = emissive.add(unitColorY(color(g.crackGlow), data.damage.crackGlowY).mul(w.cracks))

  let clearcoat: F | null = null
  let clearcoatRoughness: F | null = null
  // Livery gloss 0 (chalked paint) zeroes the clearcoat through the uniform; the pipeline stays shared.
  if (tier.clearcoat) {
    clearcoat = ctx.tune.clearcoat.mul(pal.uGloss).mul(base.painted).mul(float(1).sub(primer)).mul(float(1).sub(w.soot)).mul(float(1).sub(w.scorch)).mul(float(1).sub(grime.mul(0.6)))
    clearcoatRoughness = ctx.tune.clearcoatRoughness.add(grime.mul(0.3))
  }
  return { color: albedo, roughness: rough, metalness: saturate(metal), ao, normal, emissive, clearcoat, clearcoatRoughness }
}

/**
 * Decal ink: the quad's zone (ECL_Mask.a on MAT_decal) picks the ink pair and the atlas R picks within it
 * (tex.decals flags, A6: R = 1 first ink, 0 second). Zone 0: decalInk / decalInkAlt; ⅓: decalInkAlt / decalInk;
 * ⅔: hazardA / hazardB (the chevron stripes alternate R); 1: soot (scorch fans).
 */
export function decalInk(pal: PaletteUniforms, zone: F, atlasR: F): V3 {
  const r = smoothstep(0.25, 0.75, atlasR)
  const ink = mix(asV3(pal.uDecalInkAlt), asV3(pal.uDecalInk), r)
  const alt = mix(asV3(pal.uDecalInk), asV3(pal.uDecalInkAlt), r)
  const hazard = mix(asV3(pal.uHazardB), asV3(pal.uHazardA), r)
  return select(zone.lessThan(0.5), ink, select(zone.lessThan(1.5), alt, select(zone.lessThan(2.5), hazard, asV3(pal.uSoot))))
}

